import { parseProofs } from './proofs';

// Expense (DEBIT) entries require either an uploaded proof or a remark. The
// remark may be any length: the old 10-character minimum was a proxy for
// effort that keyboard-mashing defeated ('687684684686' cleared it), while
// genuinely short reasons like 'Toll' did not. Remarks are now picked from a
// shared list, so consistency comes from reusing real wording rather than
// from a character count. Mirrors the server-side check in
// backend/app/crud/financial.py — kept here only for fast client-side
// feedback before the network round-trip; the API is the source of truth.
export function isDebitCompliant(
  transactionType: string,
  proof: string | null | undefined,
  remarks: string | null | undefined
): boolean {
  if (transactionType !== 'DEBIT') return true;
  // proof holds either one URL or a JSON list of up to 5 — an empty list is
  // stored as null, but parse rather than trust truthiness.
  const hasProof = parseProofs(proof).length > 0;
  const hasValidRemark = !!remarks && remarks.trim().length > 0;
  return hasProof || hasValidRemark;
}

// Fields whose change means the entry's original proof/remark no longer
// actually justifies what's being saved — e.g. bumping the amount shouldn't
// silently keep riding on the receipt for the old amount.
const MATERIAL_EDIT_FIELDS = ['amount', 'particular', 'ledger_category_id', 'ledger_subcategory_id', 'payment_mode'] as const;

export interface EditableRow {
  isNew?: boolean;
  transaction_type: string;
  amount: string | number;
  particular?: string | null;
  ledger_category_id: string | number;
  ledger_subcategory_id?: string | number;
  payment_mode?: string;
  proof?: string | null;
  remarks?: string | null;
  _original?: Record<string, any>;
}

// An existing DEBIT row whose amount/category/etc. was edited must come with
// either a freshly uploaded proof or remarks that were actually changed this
// session — mirrors the server-side check in update_transaction().
export function needsFreshJustification(row: EditableRow): boolean {
  if (row.isNew || row.transaction_type !== 'DEBIT' || !row._original) return false;

  const materiallyChanged = MATERIAL_EDIT_FIELDS.some(
    field => String((row as any)[field] ?? '') !== String(row._original![field] ?? '')
  );
  if (!materiallyChanged) return false;

  const proofChanged = (row.proof ?? null) !== (row._original.proof ?? null);
  const remarksChanged = (row.remarks ?? '') !== (row._original.remarks ?? '');
  return !(proofChanged || remarksChanged);
}

export const COMPLIANCE_MESSAGE =
  'Expenses require a proof or a remark — edited expenses need a new one explaining the change.';
